import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { formatDateKey, parseDateKey, todayKey } from "@/lib/time";

export const HOME_SELECTED_DATE_KEY = "home-selected-date";
export const CALENDAR_SELECTED_DATE_KEY = "calendar-selected-date";
export const CALENDAR_COLLAPSED_KEY = "home-calendar-collapsed";
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function storedCalendarDate(storageKey = HOME_SELECTED_DATE_KEY) {
  if (typeof localStorage === "undefined") return todayKey();
  const saved = localStorage.getItem(storageKey);
  return saved || todayKey();
}

type CalendarPanelProps = {
  selectedDate: string;
  cursor: Date;
  collapsed: boolean;
  onSelectedDateChange: (date: string) => void;
  onCursorChange: (cursor: Date) => void;
  onCollapsedChange: (collapsed: boolean | ((current: boolean) => boolean)) => void;
  onDayClick?: (date: string) => void;
};

export default function CalendarPanel({
  selectedDate,
  cursor,
  collapsed,
  onSelectedDateChange,
  onCursorChange,
  onCollapsedChange,
  onDayClick,
}: CalendarPanelProps) {
  const today = todayKey();
  const todayDate = parseDateKey(today);
  const currentWeekStart = new Date(todayDate);
  currentWeekStart.setDate(todayDate.getDate() - todayDate.getDay());
  const currentWeekEnd = new Date(todayDate);
  currentWeekEnd.setDate(todayDate.getDate() + (6 - todayDate.getDay()));
  const currentWeekStartKey = formatDateKey(currentWeekStart);
  const currentWeekEndKey = formatDateKey(currentWeekEnd);
  const monthLabel = cursor.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const { startKey, endKey, days, leadingBlanks } = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const allDays: Date[] = [];
    for (let d = 1; d <= end.getDate(); d++) {
      allDays.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    return {
      startKey: formatDateKey(start),
      endKey: formatDateKey(end),
      days: allDays,
      leadingBlanks: start.getDay(),
    };
  }, [cursor]);

  const selectedWeekDays = useMemo(() => {
    const selected = parseDateKey(selectedDate);
    const start = new Date(selected);
    start.setDate(selected.getDate() - selected.getDay());
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, [selectedDate]);

  const summaryQuery = trpc.day.monthSummary.useQuery({
    startDate: startKey,
    endDate: endKey,
  });

  function setDay(date: string, dateValue: Date) {
    onSelectedDateChange(date);
    onCursorChange(startOfMonth(dateValue));
    onDayClick?.(date);
  }

  function moveCalendar(delta: number) {
    if (!collapsed) {
      onCursorChange(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
      return;
    }
    const next = parseDateKey(selectedDate);
    next.setDate(next.getDate() + delta * 7);
    const nextKey = formatDateKey(next);
    onSelectedDateChange(nextKey);
    onCursorChange(startOfMonth(next));
  }

  return (
    <>
      <header className="ui-header flex items-end justify-between gap-6">
        <h1 className="ui-h1 font-display text-4xl sm:text-5xl tracking-tight" style={{ color: "black" }}>
          {monthLabel}
        </h1>
        <div className="ui-controls flex items-center gap-1">
          <button
            className="ui-back-btn h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            onClick={() => moveCalendar(-1)}
            aria-label={collapsed ? "Previous week" : "Previous month"}
          >
            <ChevronLeft className="ui-icon h-4 w-4" />
          </button>
          <button
            className="ui-today-btn h-9 px-3 text-xs uppercase tracking-wider rounded-full hover:bg-accent transition-colors"
            onClick={() => {
              const nextToday = todayKey();
              onCursorChange(startOfMonth(new Date()));
              onSelectedDateChange(nextToday);
            }}
          >
            Today
          </button>
          <button
            className="ui-back-btn h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            onClick={() => moveCalendar(1)}
            aria-label={collapsed ? "Next week" : "Next month"}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="ui-calendar bg-card border border-border/60 rounded-2xl p-3 sm:p-5">
        <button
          className="ui-calendar-header grid w-full grid-cols-7 gap-1 mb-2"
          type="button"
          onClick={() => onCollapsedChange(value => !value)}
          aria-expanded={!collapsed}
        >
          {WEEKDAYS.map((d, i) => (
            <div
              className={`text-[10px] uppercase tracking-wider text-muted-foreground text-center py-2 ${i === todayDate.getDay() ? "font-semibold text-foreground" : ""}`}
              key={i}
            >
              {i === todayDate.getDay() ? (
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-black text-white" aria-label={`Current day ${d}`}>
                  {d}
                </span>
              ) : d}
            </div>
          ))}
        </button>
        <div className="ui-week grid grid-cols-7 gap-1">
          {collapsed ? null : Array.from({ length: leadingBlanks }).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          {(collapsed ? selectedWeekDays : days).map(d => {
            const key = formatDateKey(d);
            const summary = summaryQuery.data?.[key];
            const isToday = key === today;
            const isSelected = key === selectedDate;
            const cellIndex = leadingBlanks + days.findIndex(day => formatDateKey(day) === key);
            const isCurrentWeek = key >= currentWeekStartKey && key <= currentWeekEndKey;
            const isWeekStart = cellIndex % 7 === 0;
            const total = summary?.total ?? 0;
            const done = summary?.done ?? 0;
            const skipped = summary?.skipped ?? 0;
            return (
              <button
                key={key}
                onClick={() => setDay(key, d)}
                className={`ui-day-btn relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 border transition-all hover:bg-accent ${
                  isSelected
                    ? "ui-btn-selected border-foreground bg-foreground text-background hover:bg-foreground/90"
                    : isToday
                      ? "ui-today border-foreground/50 bg-accent/50"
                      : "border-transparent bg-background"
                }`}
              >
                {isCurrentWeek && isWeekStart && <span className="absolute -left-3 h-1.5 w-1.5 rounded-full " aria-hidden="true" />}
                <span className={`font-display text-base sm:text-lg leading-none ${isSelected ? "text-background" : ""}`}>
                  {d.getDate()}
                </span>
                {total > 0 ? (
                  <span
                    className={`text-[10px] sm:text-[11px] tabular-nums ${
                      isSelected
                        ? "text-background/80"
                        : done === total
                          ? "text-foreground/70"
                          : "text-muted-foreground"
                    }`}
                  >
                    {done}/{total - skipped}
                  </span>
                ) : (
                  <span className="text-[10px] text-transparent">.</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
